/**
 * Bewertung & Wirtschaftlichkeit (Admin-only step) — clean layout
 * ----------------------------------------------------------------
 * Eine einzige Karte, mit dezenten Divider-Sektionen:
 *  • Werte (WBW · Restwert · Minderwert · Aufwand)
 *  • Wirtschaftlichkeits-Banner
 *  • Reparatur (Beurteilung · Freigabe · Reparatur-/Wiederbeschaffungsdauer)
 *  • Ausfall & Mietwagen (Klasse · Tagessätze · Tage · NA · Abschlepp · Summen)
 */
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Scale, Save, Loader2, TrendingDown, CheckCircle2, AlertTriangle, XCircle, Sparkles,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const fmtEur = (v) => (Number(v || 0)).toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });

const DECISION_META = {
  wirtschaftlich: { color: "emerald", icon: CheckCircle2,  label: "Reparatur wirtschaftlich",     hint: "Reparaturkosten ≤ 100 % WBW" },
  regel_130:      { color: "amber",   icon: AlertTriangle, label: "130 %-Regel greift",            hint: "Reparatur zulässig, wenn Halter ≥ 6 Monate weiterführt" },
  totalschaden:   { color: "rose",    icon: XCircle,       label: "Wirtschaftlicher Totalschaden", hint: "Reparaturkosten > 130 % WBW — nur Wiederbeschaffungsaufwand wird erstattet" },
};

const REPARATURBEURTEILUNG_OPTIONS = [
  "Reparatur wirtschaftlich",
  "Reparatur grenzwirtschaftlich (130 %-Regel)",
  "Wirtschaftlicher Totalschaden",
  "Bagatellschaden",
  "Reparatur unwirtschaftlich",
  "Reparatur nicht möglich",
];

const REPARATURFREIGABE_OPTIONS = [
  "freigegeben",
  "freigegeben mit Auflagen",
  "nicht freigegeben",
  "Reparaturfreigabe nicht erforderlich",
];

const RENTAL_CATEGORIES = [
  { v: "A", l: "A — Kleinstwagen" },
  { v: "B", l: "B — Kleinwagen" },
  { v: "C", l: "C — Kompakt" },
  { v: "D", l: "D — Mittelklasse" },
  { v: "E", l: "E — obere Mittelklasse" },
  { v: "F", l: "F — Oberklasse" },
  { v: "G", l: "G — Kombi" },
  { v: "H", l: "H — Van / 7-Sitzer" },
  { v: "I", l: "I — SUV" },
  { v: "J", l: "J — Luxus" },
  { v: "K", l: "K — Transporter" },
];

export default function ValuationStep({
  caseId, vehicle, accountingValue,
  value, calculation, ausfallValue, onChange, onAusfallChange, onAccountingChange,
  onSave, saving = false, readOnly = false,
}) {
  const val = value || {};
  const a = ausfallValue || {};
  const acc = accountingValue || {};
  const veh = vehicle || {};
  const [fetchingMirror, setFetchingMirror] = useState(false);
  const [fetchingValuation, setFetchingValuation] = useState(false);

  const fetchValuation = async () => {
    if (!caseId) { toast.error("Akte muss zuerst gespeichert werden."); return; }
    const hasVin = (veh.vin || "").trim().length >= 11;
    if (!hasVin) {
      toast.error("Bitte FIN (17-stellig) im Fahrzeug-Step eintragen.");
      return;
    }
    setFetchingValuation(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/dat/valuate/create-dossier`);
      const d = (data.valuation || {});
      // Trage WBW + Restwert aus DAT direkt in die Form-Felder ein
      const next = { ...val };
      if (d.replacement_value) next.replacement_value = Number(d.replacement_value);
      if (d.restwert) next.residual_value = Number(d.restwert);
      onChange?.(next);
      const used = data.reused ? "erneut verwendet" : "neu erstellt";
      toast.success(
        `Bewertung ${used} — WBW: ${d.replacement_value || "—"} € · Restwert: ${d.restwert || "—"} €`
      );
    } catch (e) {
      toast.error(apiError(e, "DAT-Bewertung fehlgeschlagen"));
    } finally {
      setFetchingValuation(false);
    }
  };

  const fetchRentalMirror = async () => {
    if (!caseId) { toast.error("Akte muss zuerst gespeichert werden."); return; }
    const hasVin = (veh.vin || "").trim().length >= 11;
    const hasEuro = (veh.dat_europa_code || "").trim().length > 0;
    if (!hasVin && !hasEuro) {
      toast.error("Bitte FIN oder DAT-€uropa-Code im Fahrzeug-Step eintragen.");
      return;
    }
    setFetchingMirror(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/rental-mirror`, {});
      const m = data.rental_mirror || {};
      // Synchronisiere die UI-Felder mit den frisch ermittelten Werten.
      const newKlasse = m.rental_car_class_adjusted || m.rental_car_class;
      if (newKlasse) {
        onAusfallChange?.({ ...a, rental_category: String(newKlasse) });
      }
      if (m.daily_loss_of_use) {
        onAusfallChange?.({
          ...a,
          rental_category: String(newKlasse || a.rental_category || ""),
          daily_rate: Number(m.daily_loss_of_use),
        });
      }
      // Persistierte accounting-Daten lokal mitführen.
      if (data.case?.accounting && onAccountingChange) {
        onAccountingChange(data.case.accounting);
      }
      toast.success(`Mietwagenspiegel abgerufen — Klasse ${newKlasse || "?"} · Nutzungsausfall ${m.daily_loss_of_use || "—"} €/Tag`);
    } catch (e) {
      toast.error(apiError(e, "Mietwagenspiegel fehlgeschlagen"));
    } finally {
      setFetchingMirror(false);
    }
  };

  const repairGross = Number(calculation?.summary?.total_gross || 0);
  const wbw = Number(val.replacement_value || 0);
  const restwert = Number(val.residual_value || 0);
  const compensation = Math.max(wbw - restwert, 0);

  let decision = null;
  if (wbw > 0) {
    const ratio = repairGross / wbw;
    decision = ratio <= 1.0 ? "wirtschaftlich" : (ratio <= 1.3 ? "regel_130" : "totalschaden");
  }
  const dec = decision ? DECISION_META[decision] : null;

  const set = (k, v) => onChange({ ...val, [k]: v });
  const setA = (k, v) => onAusfallChange?.({ ...a, [k]: v });

  const nutzungsausfall = Math.round((Number(a.daily_rate || 0) * Number(a.repair_duration_days || 0)) * 100) / 100;
  const mietwagen = Math.round((Number(a.rental_daily_rate || 0) * Number(a.rental_days_count || 0)) * 100) / 100;

  return (
    <div className="w-full" data-testid="step-valuation">
      {/* Single unified card */}
      <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden">

        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-200 flex items-center gap-4">
          <div className="h-10 w-10 rounded-xl brand-gradient-soft flex items-center justify-center shrink-0">
            <Scale className="h-5 w-5 text-cyan-700" />
          </div>
          <div>
            <div className="font-display text-xl font-bold tracking-tight">Bewertung & Wirtschaftlichkeit</div>
            <div className="text-sm text-zinc-500">Werte · Wirtschaftlichkeit · Reparatur · Ausfall</div>
          </div>
        </div>

        {/* Werte */}
        <Section title="Werte">
          {/* SilverDAT 3 valuateExpert — Bewertung automatisch ermitteln */}
          <div className="mb-5 rounded-xl border border-indigo-200 bg-indigo-50/50 p-4 flex flex-col sm:flex-row gap-4 sm:items-center" data-testid="val-dat-valuation">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-indigo-700 flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> SilverDAT 3 valuateExpert
              </div>
              <div className="font-display text-base font-bold text-zinc-900 mt-1">
                Wiederbeschaffungswert &amp; Restwert automatisch ermitteln
              </div>
              <p className="text-[12px] text-zinc-600 mt-1">
                Legt ein DAT-Bewertungs-Dossier auf Basis der FIN an. Die Werte werden direkt in die Felder unten übernommen.
              </p>
              {val.dat_pulled_at && (
                <div className="mt-2 text-[11px] text-zinc-500 flex flex-wrap gap-x-4 gap-y-0.5">
                  <span>Letzter Abruf: <strong className="text-zinc-700">{new Date(val.dat_pulled_at).toLocaleString("de-DE")}</strong></span>
                  {val.dat_valuation?.replacement_value && <span>DAT-WBW: <strong className="text-indigo-700">{val.dat_valuation.replacement_value} €</strong></span>}
                  {val.dat_valuation?.restwert && <span>DAT-Restwert: <strong className="text-indigo-700">{val.dat_valuation.restwert} €</strong></span>}
                </div>
              )}
            </div>
            <Button
              type="button"
              onClick={fetchValuation}
              disabled={readOnly || fetchingValuation}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="val-fetch-dat-valuation"
            >
              {fetchingValuation ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {val.dat_pulled_at ? "Neu berechnen" : "Bewertung abrufen"}
            </Button>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            <Field label="Wiederbeschaffungswert (brutto)">
              <CurrencyInput value={val.replacement_value} onChange={(v) => set("replacement_value", v)} disabled={readOnly} testid="val-wbw" />
            </Field>
            <Field label="Restwert (brutto)">
              <CurrencyInput value={val.residual_value} onChange={(v) => set("residual_value", v)} disabled={readOnly} testid="val-restwert" />
            </Field>
            <Field label="Merkantiler Minderwert">
              <CurrencyInput value={val.market_depreciation} onChange={(v) => set("market_depreciation", v)} disabled={readOnly} testid="val-minderwert" />
            </Field>
            <Field label="Wiederbeschaffungsaufwand">
              <ReadOnlyValue value={fmtEur(compensation)} testid="val-compensation" />
            </Field>
          </div>
        </Section>

        {/* Wirtschaftlichkeit Banner — kompakt */}
        <div
          className={`mx-6 my-4 rounded-xl border px-4 py-3 flex items-center gap-3 ${dec ? `border-${dec.color}-200 bg-${dec.color}-50` : "border-zinc-200 bg-zinc-50"}`}
          data-testid="val-decision"
        >
          <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${dec ? `bg-${dec.color}-100` : "bg-zinc-100"}`}>
            {dec ? <dec.icon className={`h-4 w-4 text-${dec.color}-700`} /> : <TrendingDown className="h-4 w-4 text-zinc-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`font-semibold text-sm ${dec ? `text-${dec.color}-900` : "text-zinc-700"}`}>
              {dec ? dec.label : "Wirtschaftlichkeit — noch keine Daten"}
            </div>
            <div className="text-[11px] text-zinc-500">{dec ? dec.hint : "Tragen Sie WBW + Reparaturkosten ein."}</div>
          </div>
          <div className="hidden md:flex gap-4 text-[11px] tabular-nums">
            <Stat label="Reparatur"  value={fmtEur(repairGross)} />
            <Stat label="WBW"        value={fmtEur(wbw)} />
            <Stat label="Verhältnis" value={wbw > 0 ? `${((repairGross / wbw) * 100).toFixed(0)} %` : "—"} />
          </div>
        </div>

        {/* Reparatur */}
        <Section title="Reparatur">
          <div className="grid md:grid-cols-4 gap-4">
            <Field label="Reparaturbeurteilung">
              <DropdownInput value={val.reparaturbeurteilung} onChange={(v) => set("reparaturbeurteilung", v)} options={REPARATURBEURTEILUNG_OPTIONS} disabled={readOnly} testid="val-reparaturbeurteilung" />
            </Field>
            <Field label="Reparaturfreigabe">
              <DropdownInput value={val.reparaturfreigabe} onChange={(v) => set("reparaturfreigabe", v)} options={REPARATURFREIGABE_OPTIONS} disabled={readOnly} testid="val-reparaturfreigabe" />
            </Field>
            <Field label="Reparaturdauer (Arbeitstage)">
              <NumberInput value={a.repair_duration_days} onChange={(v) => setA("repair_duration_days", v)} step="1" disabled={readOnly || !onAusfallChange} testid="val-repair-days" />
            </Field>
            <Field label="Wiederbeschaffungsdauer (Kalendertage)">
              <NumberInput value={val.wiederbeschaffungsdauer} onChange={(v) => set("wiederbeschaffungsdauer", v)} step="1" disabled={readOnly} testid="val-wbeschaffungsdauer" />
            </Field>
          </div>
        </Section>

        {/* Ausfall & Mietwagen */}
        <Section title="Ausfall & Mietwagen">
          {/* SilverDAT Mietwagenspiegel — Auto-Kalkulation */}
          <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 flex flex-col sm:flex-row gap-4 sm:items-center" data-testid="val-rental-mirror">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700 flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> SilverDAT Mietwagenspiegel
              </div>
              <div className="font-display text-base font-bold text-zinc-900 mt-1">
                Mietwagenklasse &amp; Nutzungsausfall automatisch ermitteln
              </div>
              <p className="text-[12px] text-zinc-600 mt-1">
                Auf Basis der FIN bzw. des DAT-€uropa-Codes wird die Mietwagenklasse abgerufen und automatisch um die Alters-Anpassung reduziert (≥5 Jahre −1 Klasse, ≥10 Jahre −2 Klassen).
              </p>
              {(acc.rental_mirror_fetched_at || acc.rental_car_class) && (
                <div className="mt-2 text-[11px] text-zinc-500 flex flex-wrap gap-x-4 gap-y-0.5">
                  {acc.rental_car_class && <span>DAT-Klasse: <strong className="text-zinc-700">{acc.rental_car_class}</strong></span>}
                  {acc.rental_car_class_adjusted && acc.rental_car_class_adjusted !== acc.rental_car_class && (
                    <span>Nach Alter: <strong className="text-emerald-700">{acc.rental_car_class_adjusted}</strong></span>
                  )}
                  {acc.vehicle_age_years != null && <span>Fzg-Alter: <strong className="text-zinc-700">{acc.vehicle_age_years} J.</strong></span>}
                  {acc.loss_of_use_class && <span>NA-Gruppe: <strong className="text-zinc-700">{acc.loss_of_use_class}</strong></span>}
                </div>
              )}
            </div>
            <Button
              type="button"
              onClick={fetchRentalMirror}
              disabled={readOnly || fetchingMirror}
              className="brand-gradient-bg text-white"
              data-testid="val-fetch-rental-mirror"
            >
              {fetchingMirror ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {acc.rental_mirror_fetched_at ? "Aktualisieren" : "Mietwagenspiegel abrufen"}
            </Button>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <Field label="Mietwagenklasse">
              <DropdownInput value={a.rental_category} onChange={(v) => setA("rental_category", v)} options={RENTAL_CATEGORIES.map((c) => ({ v: c.v, l: c.l }))} disabled={readOnly || !onAusfallChange} testid="val-rental-cat" />
            </Field>
            <Field label="Mietwagen-Tagessatz">
              <CurrencyInput value={a.rental_daily_rate} onChange={(v) => setA("rental_daily_rate", v)} disabled={readOnly || !onAusfallChange} testid="val-rental-rate" />
            </Field>
            <Field label="Mietwagen-Tage">
              <NumberInput value={a.rental_days_count} onChange={(v) => setA("rental_days_count", v)} step="1" disabled={readOnly || !onAusfallChange} testid="val-rental-days" />
            </Field>
            <Field label="Summe Mietwagen">
              <ReadOnlyValue value={fmtEur(mietwagen)} testid="val-mietwagen-total" />
            </Field>

            <Field label="NA-Tagessatz (Schwacke)">
              <CurrencyInput value={a.daily_rate} onChange={(v) => setA("daily_rate", v)} disabled={readOnly || !onAusfallChange} testid="val-na-rate" />
            </Field>
            <Field label="Summe Nutzungsausfall">
              <ReadOnlyValue value={fmtEur(nutzungsausfall)} testid="val-na-total" />
            </Field>
            <Field label="Abschleppkosten">
              <CurrencyInput value={a.towing_cost} onChange={(v) => setA("towing_cost", v)} disabled={readOnly || !onAusfallChange} testid="val-towing-cost" />
            </Field>
          </div>
        </Section>

        {/* Anmerkung */}
        <Section title="Anmerkung">
          <Textarea
            rows={2}
            value={val.note || ""}
            onChange={(e) => set("note", e.target.value)}
            disabled={readOnly}
            placeholder="z. B. Restwert ermittelt aus Restwertbörse mit 3 Geboten…"
            data-testid="val-note"
          />
        </Section>

        {onSave && !readOnly && (
          <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50/50 flex justify-end">
            <Button onClick={onSave} disabled={saving} className="brand-gradient-bg text-white" data-testid="val-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Bewertung speichern
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Sub-components ----

function Section({ title, children }) {
  return (
    <div className="px-6 py-4 border-b border-zinc-100 last:border-b-0">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-zinc-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] text-zinc-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function CurrencyInput({ value, onChange, disabled, testid }) {
  // Zeigt KEINE aufgezwungene "0" an: Feld ist leer, solange kein Wert
  // erfasst wurde, und lässt sich beim Tippen komplett leeren.
  const [text, setText] = useState(Number(value) ? String(value) : "");
  React.useEffect(() => {
    const typed = text === "" ? 0 : parseFloat(text) || 0;
    const ext = Number(value) || 0;
    if (typed !== ext) setText(ext ? String(ext) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="relative">
      <Input
        type="number"
        step="0.01"
        min="0"
        className="pr-7 text-right tabular-nums"
        value={text}
        placeholder="—"
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          onChange(raw === "" ? 0 : parseFloat(raw) || 0);
        }}
        disabled={disabled}
        data-testid={testid}
      />
      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-400 pointer-events-none">€</span>
    </div>
  );
}

function NumberInput({ value, onChange, step = "1", disabled, testid }) {
  const [text, setText] = useState(Number(value) ? String(value) : "");
  React.useEffect(() => {
    const typed = text === "" ? 0 : parseFloat(text) || 0;
    const ext = Number(value) || 0;
    if (typed !== ext) setText(ext ? String(ext) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <Input
      type="number"
      step={step}
      min="0"
      className="text-right tabular-nums"
      value={text}
      placeholder="—"
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        onChange(raw === "" ? 0 : parseFloat(raw) || 0);
      }}
      disabled={disabled}
      data-testid={testid}
    />
  );
}

function ReadOnlyValue({ value, testid }) {
  return (
    <div className="h-10 px-3 flex items-center justify-end rounded-md border border-zinc-200 bg-zinc-50 font-mono text-sm font-semibold text-zinc-900 tabular-nums" data-testid={testid}>
      {value}
    </div>
  );
}

function DropdownInput({ value, onChange, options, disabled, testid }) {
  // Options can be ["a", "b"] or [{v, l}, …]
  const normalized = options.map((o) => (typeof o === "string" ? { v: o, l: o } : o));
  return (
    <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>
        {normalized.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-mono font-semibold text-zinc-900">{value}</div>
    </div>
  );
}
